export interface CommunityUserBrief {
  id: number;
  username: string;
  full_name: string;
  first_name: string;
  display_name: string;
  avatar_url?: string | null;
  is_admin: boolean;
  /**
   * "Founding Member" — one of the first 1.000 paying WolfPack Health Club
   * members. Public by design: the badge is shown next to the author's name on
   * threads and comments. Undefined on backends that predate the feature.
   */
  founding_member?: boolean;
  /** The founding number, 1..1000 (shown in the badge tooltip). */
  founding_member_number?: number | null;
  /**
   * "Founding Practitioner" — the same honour for the first 1.000 Practitioner
   * Network members, and equally public. A separate distinction: a member may wear
   * both badges. Undefined on backends whose community payloads don't carry it,
   * in which case the badge simply never renders.
   */
  founding_practitioner?: boolean;
  /** The founding-professional number, 1..1000 (shown in the badge tooltip). */
  founding_practitioner_number?: number | null;
  /** Muted in the community — only ever true for admin viewers. */
  community_hidden?: boolean;
  /** Whether the viewer may mute/unmute this user. */
  can_hide?: boolean;
  /**
   * Role badges, already filtered by the backend for where this content is shown
   * (visibility scope, F13) — render as-is. Undefined on older backends.
   */
  badges?: CommunityRoleBadge[];
}

/** A role badge next to an author's name. `color` is a CoreUI theme colour name or a CSS colour. */
export interface CommunityRoleBadge {
  key: string;
  label: string;
  color: string | null;
}

// --- Network structure (Phase 0: groups → channels → permissions) -------------

export type CommunityChannelMode = 'discussion' | 'news' | 'qa' | 'chat';

/** Why a group is shown as a locked teaser instead of being enterable. */
export type CommunityLockedReason = 'health_club_membership' | 'role_required';

/**
 * The viewer's rights in one channel, resolved by the backend (CommunityAccess).
 * Every flag is present; the UI never derives a right from `is_admin`.
 */
export interface CommunityChannelPermissions {
  view: boolean;
  read: boolean;
  create_thread: boolean;
  comment: boolean;
  react: boolean;
  upload_image: boolean;
  upload_file: boolean;
  embed_video: boolean;
  mention: boolean;
  pin: boolean;
  manage_links: boolean;
  edit_own: boolean;
  edit_any: boolean;
  delete_own: boolean;
  delete_any: boolean;
  moderate: boolean;
}

export interface CommunityRoleBrief {
  key: string;
  name: string;
  badge_label: string | null;
  badge_color: string | null;
  visibility: string;
  /** Only sent for opt-in roles (the "I am interested in …" card). */
  description?: string | null;
}

export interface CommunityGroupBrief {
  id: number;
  key: string;
  name: string;
  icon: string | null;
}

export interface CommunityChannel {
  id: number;
  group_id: number;
  key: string;
  name: string;
  description: string | null;
  mode: CommunityChannelMode;
  read_only: boolean;
  archived: boolean;
  /** Migrated board of the previous forum: read-only, with the old topics as a filter. */
  legacy: boolean;
  system: boolean;
  position: number;
  allowed_uploads: string[];
  reaction_set: string[];
  /** Null when the viewer may not read the channel (locked teaser). */
  thread_count: number | null;
  /**
   * Threads holding something new for the viewer (never opened, or replied to
   * since their last visit). Null on archived boards -- nothing is written
   * there any more, so they keep showing `thread_count` instead. Undefined on
   * older backends.
   */
  unread_count?: number | null;
  permissions: CommunityChannelPermissions;
  /** Present on channel detail / thread list responses. */
  group?: CommunityGroupBrief;
  /**
   * Fixed links shown at the top of the channel. Readers get the active ones;
   * viewers with `permissions.manage_links` also the inactive ones.
   */
  links?: CommunityChannelLink[];
  /** Whether the viewer subscribed to this channel (Phase 4 notifications). Undefined on older backends. */
  subscribed?: boolean;
  /**
   * Private Room: only granted participants and the responsible admins see the
   * channel at all. Undefined on older backends -- treat as not private.
   */
  private?: boolean;
  /** Private Room only: participants may read but not write. */
  participants_read_only?: boolean;
  /** When the channel was turned private (irreversible); null if created private or public. */
  privatized_at?: string | null;
}

/** A pinned link at the top of a channel (customer addition 08.09.2026). */
export interface CommunityChannelLink {
  id: number;
  channel_id: number;
  title: string;
  description: string | null;
  url: string;
  position: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ChannelLinkPayload {
  title?: string;
  description?: string | null;
  url?: string;
  active?: boolean;
  position?: number;
}

export interface CommunityGroup {
  id: number;
  key: string;
  name: string;
  description: string | null;
  icon: string | null;
  visibility: string;
  position: number;
  system: boolean;
  archived: boolean;
  /** Listed as a teaser only: the viewer lacks the role that opens it. */
  locked: boolean;
  locked_reason: CommunityLockedReason | null;
  role_keys: string[];
  channels: CommunityChannel[];
}

/**
 * A sanction or warning in force against the viewer (overview `sanctions` /
 * `warnings`). `scope_type` null = network-wide.
 */
export interface CommunitySanction {
  id: number;
  kind: 'warn' | 'mute' | 'ban' | 'hide';
  scope_type: 'CommunityGroup' | 'CommunityChannel' | null;
  scope_id: number | null;
  scope_name: string | null;
  reason: string;
  until: string | null;
  created_at: string;
}

export interface CommunityOverview {
  enabled: boolean;
  is_admin: boolean;
  /** Admins and area admins: the admin page is offered. */
  can_manage?: boolean;
  /** Moderates at least one channel: the moderation page is offered. */
  can_moderate?: boolean;
  muted: boolean;
  /** Sanctions currently in force against the viewer (Phase 2). */
  sanctions?: CommunitySanction[];
  /** The viewer's last warnings, newest first (Phase 2). */
  warnings?: CommunitySanction[];
  roles: CommunityRoleBrief[];
  /** Roles the viewer may join themselves (e.g. sales_interest). */
  opt_in_roles: CommunityRoleBrief[];
  groups: CommunityGroup[];
  /** Set when a group is locked for `health_club_membership`. */
  purchase_url: string | null;
  /** Whether a machine translation provider is configured (Phase 5, §15/F10); hides every "Translate" button when false. */
  translation_available?: boolean;
  /**
   * The viewer's per-source-language translation preferences, delivered with the
   * overview so the thread page needs no second call. Absent on older backends;
   * a language not listed means the default, `ask`.
   */
  translation_preferences?: Record<string, CommunityTranslationStoredMode>;
}

/** Old topic of a migrated board, offered as a read-only filter in an archive channel. */
export interface CommunityLegacyTopic {
  id: number;
  name: string;
  locale: string;
  thread_count: number;
}

export interface CommunityChannelDetailResponse {
  channel: CommunityChannel;
  topics: CommunityLegacyTopic[];
}

/** Channel summary embedded in a thread. */
export interface CommunityThreadChannelBrief {
  id: number;
  key: string;
  name: string;
  mode: CommunityChannelMode;
  archived: boolean;
  read_only: boolean;
  group_id: number;
  /** The channel's reaction set (Phase 2); older backends omit it. */
  reaction_set?: string[];
  /** Private Room flag; older backends (and possibly the brief) omit it -- treat as not private. */
  private?: boolean;
}

export interface CommunityTopicBrief {
  id: number;
  name: string;
}

export interface CommunityHiddenUsersResponse {
  data: CommunityUserBrief[];
}

// --- Content -----------------------------------------------------------------

/**
 * Approval state of a thread or post in a channel with `requires_approval`
 * (§23). Members see only approved content plus their own pending/rejected
 * items; moderators see everything.
 */
export type CommunityApprovalStatus = 'approved' | 'pending' | 'rejected';

export interface CommunityThread {
  id: number;
  locale: string;
  title: string;
  body?: string;
  channel_id: number;
  channel: CommunityThreadChannelBrief;
  /** Legacy topic of a migrated thread; null for everything written in a channel. */
  topic_id: number | null;
  topic: CommunityTopicBrief | null;
  pinned: boolean;
  locked: boolean;
  deleted_at: string | null;
  hidden_at: string | null;
  hidden_by_id: number | null;
  created_at: string;
  updated_at: string;
  edited_at: string | null;
  user: CommunityUserBrief | null;
  is_mine: boolean;
  can_modify: boolean;
  can_delete: boolean;
  can_pin: boolean;
  can_moderate: boolean;
  /** Alias of `can_moderate`, kept for older backends. */
  can_admin: boolean;
  /**
   * Whether the current user may comment. Covers locked threads, read-only and
   * archived channels and muted users in one flag.
   */
  can_reply?: boolean;
  /** Moderators only: the author is muted, so members see nothing of this thread. */
  author_hidden?: boolean;
  masked: boolean;
  unread?: boolean;
  has_unread_replies?: boolean;
  reply_count?: number;
  /** Reaction counts by key (only keys with at least one reaction). */
  reactions?: Record<string, number>;
  /** Keys the viewer has reacted with. */
  my_reactions?: string[];
  /** Whether the viewer may react here (right `react`, channel writable). */
  can_react?: boolean;
  approval_status?: CommunityApprovalStatus;
  /** Q&A (§17, `qa` channels only): the post marked as the best answer, if any. */
  solved_post_id?: number | null;
  /** Alias of `solved_post_id != null`, for a simple badge/filter check. */
  solved?: boolean;
  /** Whether the viewer (asker or a moderator) may mark/unmark a best answer. */
  can_solve?: boolean;
}

export interface CommunityPost {
  id: number;
  thread_id: number;
  parent_post_id: number | null;
  /** Source language of the body, needed to decide whether to offer a translation. */
  locale: string;
  body: string;
  deleted_at: string | null;
  hidden_at: string | null;
  hidden_by_id: number | null;
  created_at: string;
  updated_at: string;
  edited_at: string | null;
  user: CommunityUserBrief | null;
  is_mine: boolean;
  can_modify: boolean;
  can_delete?: boolean;
  can_moderate?: boolean;
  /** Alias of `can_moderate`, kept for older backends. */
  can_admin: boolean;
  /** Moderators only: the author is muted, so members see nothing of this post. */
  author_hidden?: boolean;
  masked: boolean;
  unread?: boolean;
  reactions?: Record<string, number>;
  my_reactions?: string[];
  can_react?: boolean;
  approval_status?: CommunityApprovalStatus;
}

/** `POST /community/reports` — a fresh report (201) or the caller's still-open one (200, `existing`). */
export interface CommunityReportResponse {
  report: { id: number; status: string };
  existing?: boolean;
}

/** `POST /community/{threads,posts}/:id/reactions` — the toggle's outcome plus the fresh counts. */
export interface CommunityReactionResponse {
  reacted: boolean;
  reaction: string;
  reactions: Record<string, number>;
  my_reactions: string[];
}

/** The fixed reaction set (F7) and the emoji each key renders as. */
export const DEFAULT_REACTION_KEYS: readonly string[] = ['thumbs_up', 'heart', 'laugh', 'fire', 'bulb'];

const REACTION_EMOJI: Record<string, string> = {
  thumbs_up: '👍',
  heart: '❤️',
  laugh: '😂',
  fire: '🔥',
  bulb: '💡'
};

/** Emoji for a reaction key; an unknown key renders as its own text. */
export function reactionEmoji(key: string): string {
  return REACTION_EMOJI[key] ?? key;
}

/** Sum of all reaction counts on a thread or post. */
export function reactionTotal(reactions: Record<string, number> | null | undefined): number {
  if (!reactions) return 0;
  return Object.values(reactions).reduce((sum, n) => sum + (Number(n) || 0), 0);
}

export interface CommunityVersionSnapshot {
  event: 'create' | 'update' | 'current';
  version_id: number | null;
  changed_at: string;
  editor_id: string | null;
  editor_name: string | null;
  state: {
    title?: string;
    body?: string;
  };
}

export interface CommunityVersionsResponse {
  versions: CommunityVersionSnapshot[];
}

export interface CommunityPostNode extends CommunityPost {
  replies: CommunityPostNode[];
}

export interface CommunityThreadListResponse {
  data: CommunityThread[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  /** The requested channel; null for the cross-channel "latest" list. */
  channel: CommunityChannel | null;
  /** Whether the viewer may start a thread in `channel` (always false without one). */
  can_create_thread: boolean;
}

export interface CommunityThreadDetailResponse {
  thread: CommunityThread;
  posts: CommunityPost[];
}

// --- Mentions, subscriptions, attachments, embeds, search, translation (Phase 4/5) --

/** `GET /community/mentionable` — members the writer may @mention in this channel. */
export interface CommunityMentionableUser {
  id: number;
  username: string;
  full_name: string;
  avatar_url?: string | null;
}

export interface CommunityMentionableResponse {
  data: CommunityMentionableUser[];
}

export interface CommunitySubscribeResponse {
  ok: boolean;
  subscribed: boolean;
}

export type CommunityAttachmentKind = 'image' | 'file';

export interface CommunityAttachment {
  id: number;
  url: string;
  kind: CommunityAttachmentKind;
  name: string;
  size: number;
  mime: string;
}

export interface CommunityAttachmentResponse {
  attachment: CommunityAttachment;
}

export interface CommunityEmbedHost {
  key: string;
  name: string;
}

/** Answer of `POST /community/pdf_import`: the converted document, nothing stored. */
export interface CommunityPdfImportResponse {
  html: string;
  pages: number;
  /** True when the PDF had more pages than the server converts in one go. */
  truncated: boolean;
}

export interface CommunityEmbedResponse {
  embed_url: string;
  html: string;
  hosts: CommunityEmbedHost[];
}

export type CommunitySearchType = 'all' | 'threads' | 'posts' | 'people';

export interface CommunitySearchThreadHit extends CommunityThread {
  excerpt: string;
}

export interface CommunitySearchPostHit extends CommunityPost {
  thread_title: string;
  channel: { id: number; name: string };
  excerpt: string;
}

export interface CommunitySearchResponse {
  query: string;
  type: CommunitySearchType;
  threads?: { total_count: number; data: CommunitySearchThreadHit[] };
  posts?: { total_count: number; data: CommunitySearchPostHit[] };
  people?: { data: CommunityUserBrief[] };
}

// --- Translation (§15, F10) ---------------------------------------------------

/**
 * Status of one (record, target locale) pair as the server reports it.
 *
 * - `ready` — a cached translation matching the current source digest exists.
 * - `pending` — a job is queued or running; poll again.
 * - `failed` — the job gave up; `reason` says why.
 * - `same_language` — the record is already written in the target locale.
 * - `unavailable` — no provider configured, or the record may not be translated
 *   (masked/deleted content is never sent to a model).
 */
export type CommunityTranslationStatus = 'ready' | 'pending' | 'failed' | 'same_language' | 'unavailable';

/**
 * What the client renders. `timeout` is purely client-side: the coordinator gives
 * up polling after two minutes and shows it like a failure, with its own wording.
 */
export type CommunityTranslationViewStatus = CommunityTranslationStatus | 'timeout';

/** Why a translation failed. `too_long` is the only one without a retry button. */
export type CommunityTranslationReason = 'providers_down' | 'too_long' | 'invalid_output' | 'error';

/** Per-source-language preference. `ask` is the default and is stored as the absence of a row. */
export type CommunityTranslationMode = 'always' | 'ask' | 'never';

/** The two modes the server actually persists; `ask` deletes the row. */
export type CommunityTranslationStoredMode = Exclude<CommunityTranslationMode, 'ask'>;

/** One element (the thread, or one post) in a batch translation answer. */
export interface CommunityTranslationItem {
  status: CommunityTranslationStatus;
  /** Thread only, and only with `status: 'ready'`. Rendered as text, never as HTML. */
  title?: string;
  /** Sanitized HTML fragment, only with `status: 'ready'`. */
  text?: string;
  provider?: string;
  reason?: CommunityTranslationReason;
  /** Seconds the server suggests waiting before polling again. */
  retry_after?: number;
}

/**
 * `POST /community/{threads,posts}/:id/translate` — the single-item endpoint.
 * `available: false` until a provider is configured.
 */
export interface CommunityTranslationResponse extends CommunityTranslationItem {
  available: boolean;
  locale: string;
  cached?: boolean;
}

/**
 * `POST /community/threads/:id/translations` — the thread page's collective
 * endpoint, also used as the polling call. Posts are keyed by their id as a
 * string (JSON object keys).
 */
export interface CommunityTranslationBatchResponse {
  available: boolean;
  locale: string;
  /** Seconds to wait before the next poll; the client backs off from there. */
  retry_after?: number;
  thread?: CommunityTranslationItem;
  posts?: Record<string, CommunityTranslationItem>;
}

/** `GET|PUT /community/translation_preferences`. */
export interface CommunityTranslationPreferencesResponse {
  preferences: Record<string, CommunityTranslationStoredMode>;
  modes: CommunityTranslationMode[];
}

/**
 * `POST /community/language_check` (§7.3) — a read-only look at what language a
 * draft appears to be written in, asked once right before it is saved.
 *
 * `expected` is what the record WOULD be stored as (the passed `locale`, else the
 * thread's language on a reply, else the writer's UI language). `mismatch` is the
 * only field the client acts on: it is `confident && detected != expected`, so a
 * shaky guess never puts a dialog in the writer's way. `available: false` (no
 * provider, or the model is down) always comes with `mismatch: false` — the check
 * fails open, it must never keep a post from being written.
 */
export interface CommunityLanguageCheckResponse {
  available: boolean;
  detected: string | null;
  confident: boolean;
  expected: string;
  mismatch: boolean;
}

/**
 * Languages a post can be written in, i.e. the backend's `SUPPORTED_LOCALES`.
 * Deliberately NOT the app's configured languages: the community is tenant-wide,
 * so a reader on an English-only brand still meets German and Spanish posts.
 * Keep in sync with `I18nService.supportedLanguages` and `User::SUPPORTED_LANGUAGES`.
 */
export const COMMUNITY_TRANSLATION_LOCALES: readonly string[] = [
  'en', 'de', 'es', 'es-MX', 'fr', 'pt', 'it', 'ru', 'cs', 'sk', 'ar'
];

/**
 * Locale comparison for the translation feature: case-insensitive, but a region
 * is part of the identity — 'es-MX' is not 'es', so a Mexican post is offered to
 * a Spanish reader (and the preference for it is its own row).
 */
export function communitySameLocale(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

// --- Errors ------------------------------------------------------------------

/** JSON `code` values the community API returns alongside 403/429/422 responses. */
export type CommunityErrorCode =
  | 'community_not_member'
  | 'community_forbidden'
  | 'community_no_permission'
  | 'community_user_hidden'
  | 'community_channel_read_only'
  | 'community_rate_limited'
  | 'community_reaction_unknown'
  | 'community_group_has_threads'
  | 'community_channel_has_threads'
  | 'reason_required'
  | 'email_not_confirmed'
  | 'community_blocked'
  | 'community_target_is_admin'
  | 'community_not_pending'
  | 'community_already_lifted'
  | 'avatar_too_large'
  | 'avatar_invalid'
  | 'community_not_qa'
  | 'community_query_too_short'
  | 'community_embed_host_not_allowed'
  | 'attachment_too_large'
  | 'attachment_invalid'
  | 'community_upload_kind_disabled'
  | 'push_disabled'
  | 'community_muted';

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<CommunityErrorCode>([
  'community_not_member',
  'community_forbidden',
  'community_no_permission',
  'community_user_hidden',
  'community_channel_read_only',
  'community_rate_limited',
  'community_reaction_unknown',
  'community_group_has_threads',
  'community_channel_has_threads',
  'reason_required',
  'email_not_confirmed',
  'community_blocked',
  'community_target_is_admin',
  'community_not_pending',
  'community_already_lifted',
  'avatar_too_large',
  'avatar_invalid',
  'community_not_qa',
  'community_query_too_short',
  'community_embed_host_not_allowed',
  'attachment_too_large',
  'attachment_invalid',
  'community_upload_kind_disabled',
  'push_disabled',
  'community_muted'
]);

/**
 * True when a body written in the editor would show the reader nothing. The editor
 * never hands back an empty string — an untouched field is `<p><br></p>` and a few
 * RETURN presses are `<p><br></p><p><br></p>…` — so a plain `.trim()` check lets a
 * reply of blank lines through. Mirrors CommunitySanitizer.content_blank? on the
 * backend: a picture, a video embed or a separator counts as content although it
 * carries no text.
 */
export function communityBodyBlank(html: string | null | undefined): boolean {
  const raw = (html ?? '').trim();
  if (!raw) return true;

  const probe = document.createElement('div');
  probe.innerHTML = raw;
  if (probe.querySelector('img, iframe, hr')) return false;

  return (probe.textContent ?? '').replace(/\u00a0/g, ' ').trim().length === 0;
}

/**
 * Plain-text rendering of a body for a one-line preview (thread list, dashboard
 * widget, moderation queue). NOT `textContent` of the parsed markup: that glues
 * adjacent blocks together — `<p>Hallo</p><p>Welt</p>` reads "HalloWelt", and a
 * post written line by line in the editor came out as one run-on word salad in
 * every preview. A space is put at every block boundary and line break before the
 * tags are dropped, then the whitespace is collapsed. Parsed with DOMParser, so no
 * `<img>` in the body is ever fetched just to build a preview. Mirrors
 * CommunitySanitizer.strip_to_plain on the backend.
 */
export function communityHtmlToText(html: string | null | undefined): string {
  if (!html) return '';
  const spaced = html.replace(/<br\s*\/?>|<hr\s*\/?>|<\/(?:p|div|li|h[1-6]|blockquote|pre|ul|ol|tr|td|th)>/gi, '$& ');
  const text = new DOMParser().parseFromString(spaced, 'text/html').body.textContent ?? '';
  return text.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Translation key for an API error: `community.errors.<code>` when the backend
 * sent a known code, else the generic fallback. Accepts the raw HttpErrorResponse.
 */
export function communityErrorKey(error: unknown): string {
  const code = (error as { error?: { code?: unknown } } | null)?.error?.code;
  return typeof code === 'string' && KNOWN_ERROR_CODES.has(code)
    ? `community.errors.${code}`
    : 'community.errors.generic';
}
